import { Component, Inject, OnInit, ViewChild, ElementRef, signal, effect } from '@angular/core';
import { CommonModule, NgIf, NgFor } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../order-service';
import { SpinnerOverlayService } from '../../Components/spinner/spinner-overlay.service';
import { CartItem } from '../product-cart-component/product-cart-service';
import { IOrder } from '../../shared/model/order.model';
import { AccountService } from '../../login/account.service';
import { CustomerService } from '../../shared/service/customer-service';
import { LoginService } from '../../login/login.service';
import { MessageService } from '../../shared/message.service';
import { environment } from '../../../../environment.prod';
import { WarehouseService } from '../../shared/service/warehouse-service';
import { IWarehouse } from '../../shared/model/warehouse.model';

@Component({
  selector: 'app-order-detail-dialog',
  imports: [CommonModule, NgIf, NgFor, TableModule, FormsModule],
  templateUrl: './order-detail-dialog.html',
  styleUrl: './order-detail-dialog.css'
})
export class OrderDetailDialog implements OnInit {
  @ViewChild('supportTextarea') supportTextarea?: ElementRef<HTMLTextAreaElement>;
  items: CartItemView[] = [];
  total = 0;
  totalItems = 0;
  itemsPerPage = 6;
  showSupportOptions: boolean = false;
  supportMessage: string = '';
  userName: string = '';
  customerName: string = '';
  activeWarehouse = signal<IWarehouse | null>(null);

  constructor(
    private orderService: OrderService,
    private spinner: SpinnerOverlayService,
    private dialogRef: MatDialogRef<OrderDetailDialog>,
    private accountService: AccountService,
    private customerService: CustomerService,
    private loginService: LoginService,
    private messageService: MessageService,
    private warehouseService: WarehouseService,
    @Inject(MAT_DIALOG_DATA) public data: IOrder
  ) {
    effect(() => {
      const warehouseCode = this.accountService.selectedWarehouseCode();
      if (warehouseCode) {
        this.loadWarehouseInfo(warehouseCode);
      } else {
        this.activeWarehouse.set(null);
      }
    });
  }

  ngOnInit() {
    this.loadUserAndCustomerData();
    if (this.data?.id) {
      this.loadItems(String(this.data.id));
    }
  }

  private loadUserAndCustomerData() {
    const user = this.accountService.userIdentity()?.user;
    if (user) {
      this.userName = user.name || '';
      const addressNumber = user.addressNumber;
      if (addressNumber) {
        this.customerService.getCustomerDataByANs([addressNumber]).subscribe({
          next: (res: any) => {
            const body = res.body as any;
            const customers = body?.customers || body?.customer || [];
            if (customers.length > 0) {
              // Adjust according to actual customer model name property, usually name or businessName
              this.customerName = customers[0].name || customers[0].businessName || customers[0].alphaName || '';
            }
          },
          error: (err) => console.error('Error loading customer', err)
        });
      }
    }
  }

  private loadItems(orderId: string) {
    this.handleLoading(true);
    const addressNumber = this.accountService.userIdentity()?.user.addressNumber;

    this.orderService.getOrderItems(orderId, addressNumber, addressNumber).subscribe({
      next: (response: any) => {
        const raw = response?.body?.items ?? response?.items ?? response ?? [];
        this.items = Array.isArray(raw) ? this.mapItems(raw) : [];
        this.total = this.items.reduce((sum, it) => sum + (it.price ?? 0) * (it.quantity ?? 0), 0);
        this.totalItems = this.items.length;
        this.handleLoading(false);
      },
      error: () => {
        this.handleLoading(false);
      }
    });
  }


  private mapItems(rawItems: any[]): CartItemView[] {
    return rawItems.map((it: any) => ({
      id: String(it.id ?? it.itemId ?? ''),
      productId: Number(it.productId ?? it.product_id ?? 0),
      productImage: it.productImage ?? it.product_image ?? '',
      name: it.name ?? '',
      description: it.description ?? '',
      dimensions: it.dimensions ?? '',
      thickness: it.thickness != null ? Number(it.thickness) : undefined,
      color: it.color ? { id: Number(it.color.id), name: it.color.name, url: it.color.url ?? '' } : undefined,
      texture: it.texture ?? '',
      faceOne: it.faceOne ?? it.face_one ?? false,
      faceTwo: it.faceTwo ?? it.face_two ?? false,
      platformCount: it.platformCount != null ? Number(it.platformCount) : 0,
      halfPlatformCount: it.halfPlatformCount != null ? Number(it.halfPlatformCount) : 0,
      quarterPlatformCount: it.quarterPlatformCount != null ? Number(it.quarterPlatformCount) : 0,
      eighthPlatformCount: it.eighthPlatformCount != null ? Number(it.eighthPlatformCount) : 0,
      // If backend provides platform sizes per item, map them directly
      platform: it.platform != null ? Number(it.platform) : undefined,
      halfPlatform: it.halfPlatform != null ? Number(it.halfPlatform) : undefined,
      quarterPlatform: it.quarterPlatform != null ? Number(it.quarterPlatform) : undefined,
      eighthPlatform: it.eighthPlatform != null ? Number(it.eighthPlatform) : undefined,
      price: it.price != null ? Number(it.price) : 0,
      quantity: it.quantity != null ? Number(it.quantity) : 0,
      deliveryDate: it.deliveryDate ?? it.delivery_date ?? undefined, // Fecha promesa
      lineStatus: it.lineStatus ?? it.line_status ?? undefined // Nuevo campo para el estado de línea
    }));
  }

  getItemSize(item: any): string {
    const features: string[] = [];

    if (item.dimensions && item.dimensions !== 'NA') {
      features.push(`${item.dimensions} mm`);
    }

    if (item.thickness) {
      features.push(`${item.thickness} mm`);
    }

    return features.length > 0 ? features.join(' / ') : '';
  }

  getItemFeatures(item: any): string {
    const features: string[] = [];

    if (item.color?.name && item.color.name !== 'NA') {
      features.push(item.color.name);
    }

    if (item.texture && item.texture !== 'NA') {
      features.push(item.texture);
    }

    return features.length > 0 ? features.join(' / ') : '';
  }

  close() {
    this.dialogRef.close();
  }

  toggleSupportOptions() {
    this.showSupportOptions = !this.showSupportOptions;
    if (!this.showSupportOptions) {
      this.supportMessage = '';
    } else {
      setTimeout(() => this.supportTextarea?.nativeElement?.focus(), 100);
    }
  }

  openSupport() {
    this.showSupportOptions = true;
    setTimeout(() => this.supportTextarea?.nativeElement?.focus(), 100);
  }

  contactWhatsApp() {
    const orderNumber = this.data?.orderNumber || '-';
    let textStr = `Hola, soy ${this.userName} de "${this.customerName}". \nSolicito soporte para el pedido #${orderNumber}`;
    if (this.supportMessage && this.supportMessage.trim()) {
      textStr += `\n\ncon el siguiente mensaje: "${this.supportMessage}"`;
    }
    const text = encodeURIComponent(textStr);
    window.open(`https://wa.me/${environment.soportePhone}?text=${text}`, '_blank');
    this.showSupportOptions = false;
    this.supportMessage = '';
  }

  contactEmail() {
    const orderNumber = this.data?.orderNumber || '-';
    const user = this.accountService.userIdentity()?.user;
    const userEmail = user?.email || '';
    const userPhone = user?.phone || '';
    this.handleLoading(true);
    this.loginService.sendSupportEmail(
      orderNumber,
      this.userName,
      this.customerName,
      userEmail,
      userPhone,
      this.supportMessage
    )
      .subscribe({
        next: () => {
          this.messageService.showSuccess('Correo enviado a soporte');
          this.showSupportOptions = false;
          this.supportMessage = '';
          this.handleLoading(false);
        },
        error: () => {
          this.messageService.showError('Error al enviar correo');
          this.handleLoading(false);
        }
      });
  }


  private handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide();
  }



  getPlatformBreakdown(item: CartItemView) {
    const parts: { label: string; count: number; units?: number; unitSize?: number }[] = [];
    if (item.platformCount && item.platformCount > 0) {
      parts.push({ label: 'Plataforma', count: item.platformCount, units: this.computeUnits(item.platformCount, item.platform), unitSize: item.platform });
    }
    if (item.halfPlatformCount && item.halfPlatformCount > 0) {
      parts.push({ label: '1/2 plataforma', count: item.halfPlatformCount, units: this.computeUnits(item.halfPlatformCount, item.halfPlatform), unitSize: item.halfPlatform });
    }
    if (item.quarterPlatformCount && item.quarterPlatformCount > 0) {
      parts.push({ label: '1/4 plataforma', count: item.quarterPlatformCount, units: this.computeUnits(item.quarterPlatformCount, item.quarterPlatform), unitSize: item.quarterPlatform });
    }
    if (item.eighthPlatformCount && item.eighthPlatformCount > 0) {
      parts.push({ label: '1/8 plataforma', count: item.eighthPlatformCount, units: this.computeUnits(item.eighthPlatformCount, item.eighthPlatform), unitSize: item.eighthPlatform });
    }
    return parts;
  }

  private computeUnits(count: number, size?: number) {
    if (size == null || isNaN(size)) return undefined;
    return count * size;
  }

  getItemCoating(item: CartItem): string {
    if (item.faceOne && !item.faceTwo) return '1 Cara';
    if (!item.faceOne && item.faceTwo) return '2 Caras';
    return 'Sin definir';
  }

  private loadWarehouseInfo(code: string) {
    this.warehouseService.findByCode(code).subscribe({
      next: (res) => {
        const responseBody = res.body as any;
        const warehouse = responseBody?.warehouse as IWarehouse;
        this.activeWarehouse.set(warehouse || null);
      },
      error: () => {
        this.activeWarehouse.set(null);
      }
    });
  }
}

interface CartItemView extends CartItem {
  platform?: number;
  halfPlatform?: number;
  quarterPlatform?: number;
  eighthPlatform?: number;
  deliveryDate?: string; // Fecha promesa
  lineStatus?: string; // Estado de línea
}